/**
 * Coverage v3 ("value check") vocabulary, shared by the Explorer coverage dashboard
 * and the per-connection coverage section in Deliveries → History. Mirrors the
 * integration-service spec `2026-10-30-coverage-v3-value-check.md` — §3 D5/D6 (states,
 * tiers), §6 (the closed reason-code set, also the API / insight keys) and §9 (API).
 * Wire values are copied verbatim; a code the UI doesn't know yet is still rendered
 * (humanised), never dropped.
 */

/** Colour tiers (D6), best first: product-level coverage ≥95 / ≥90 / ≥85 / ≥80 / <80. */
export const COVERAGE_COLOR_TIERS = ['EXCELLENT', 'GOOD', 'FAIR', 'POOR', 'CRITICAL'] as const;
export type CoverageColorTier = (typeof COVERAGE_COLOR_TIERS)[number];

/**
 * `colorTier` of a row with no valid / eligible products: there is no coverage number to colour
 * (its percentage reads `0.0`). Shown as a neutral "No data" pill, never on the percent scale.
 */
export const COVERAGE_NO_DATA_TIER = 'NONE';

/** Every `colorTier` the API sends: the five tiers, or `NONE` when there is no number. */
export type CoverageColorTierWire = CoverageColorTier | typeof COVERAGE_NO_DATA_TIER;

/** Per-product verdict (D5). `EXCLUDED` = not eligible; the other four sum to the eligible set. */
export const COVERAGE_PRODUCT_STATES = [
  'COVERED',
  'PARTIAL',
  'MISSING',
  'PENDING',
  'EXCLUDED',
] as const;

/**
 * How a run was judged (`verdictVersion`, contract 14.24): `0` marker-based, `1` value check, `2`
 * live content against Lily's latest deliverable enrichment over every delivery route. A trend
 * breaks where it changes; a `1` figure is never compared with a `2` one.
 */
export const COVERAGE_VERDICT_LIVE_VS_ENRICHMENT = 2;

/**
 * A delivery route of a coverage account (contract 14.24, `routes[]`): the coverage connection's own
 * method, or a linked delivery connection (SFTP and other file deliveries) whose
 * `config.feeds_connection_id` names it. Counts are per route, so they do not add up.
 */
export interface CoverageRoute {
  connectionId: string;
  /** `null` when the connection was deleted. */
  connectionName: string | null;
  /** Absent on the report's per-connection routes. */
  connectorCode: string | null;
  /** `API` · `SFTP` · `FILE` · `FEEDONOMICS` — the newest delivery's method on that connection. */
  method: string;
  /** `true` for a linked delivery connection, `false` for the coverage connection itself. */
  linked: boolean;
  productsDelivered: number;
  /** Of those, delivered with the expected content. */
  productsDeliveredMatching: number;
}

/**
 * "From enrichment to live" (contract 14.24, `funnel`), present from `verdictVersion: 2`:
 * eligible → with an expectation (Lily approved or delivered content) → delivered by any route →
 * live and matching (= `productsCovered`).
 */
export interface CoverageFunnel {
  eligibleProducts: number;
  productsWithExpectation: number;
  productsDeliveredAnyRoute: number;
  productsLiveMatching: number;
  /** Covered products no route has a delivery record for (another path, or older than 90 days). */
  productsCoveredWithoutDelivery: number;
}

/**
 * Why a product is not fully covered (§6). `""` on the wire means "covered, nothing to do"
 * and is never listed here. Order = how the filter tabs read when counts tie.
 */
export const COVERAGE_GAP_REASONS = [
  'OVERWRITTEN',
  'PARTIALLY_LIVE',
  'WRONG_MARKET',
  'STALE_DELIVERY',
  'ENRICHED_NOT_DELIVERED',
  'NOT_ENRICHED',
  'PENDING_PROCESSING',
  'ENRICHMENT_IN_PROGRESS',
  'LIVE_VIA_OTHER_PATH',
  'NO_ATTRIBUTES_EXPECTED',
  'READBACK_FAILED',
  'NOT_IN_CATALOG',
  'NOT_ELIGIBLE',
] as const;

/**
 * The reasons `GET …/coverage/products?reason=` accepts: every code of §6 except `NOT_IN_CATALOG`,
 * which is no per-row state (those products have no snapshot rows) and answers 422. The endpoint's
 * enum is closed, so a code this UI does not know yet would 422 too — only these open a drill-down.
 */
export const COVERAGE_FILTERABLE_REASONS: readonly string[] = COVERAGE_GAP_REASONS.filter(
  (reason) => reason !== 'NOT_IN_CATALOG',
);

/** `field` filter on the per-product drill-down (§9). `additionalFeatures` is Meta-only. */
export const COVERAGE_FIELD_FILTERS = [
  'attributes',
  'highlights',
  'descriptors',
  'additionalFeatures',
] as const;
export type CoverageFieldFilter = (typeof COVERAGE_FIELD_FILTERS)[number];

/** Longest `search` the per-product drill-down accepts (§9: 1–200 characters, else a 422). */
export const COVERAGE_SEARCH_MAX_LENGTH = 200;

/**
 * `GET|PUT /coverage/settings` payload — the subsidiary-wide "Coverage tracking" switch.
 * Default when the subsidiary has no row yet: `enabled=false`, both connectors.
 */
export interface CoverageSettings {
  enabled: boolean;
  /** Connector codes tracked when `enabled` (`GOOGLE_MERCHANT`, `META_COMMERCE`). */
  connectors: string[];
}

/** `ResponseSchema[CoverageSettings]` — the envelope both settings calls answer with. */
export interface CoverageSettingsResponse {
  success: boolean;
  message: string;
  data: CoverageSettings;
}
